<template>
	<AccordionSection
		class="LenderAccordion"
		:isValid="appOrLenderSelected ? true : null"
		:defaultExpanded="appOrLenderSelected"
	>
		<template #accordionHeader>
			<div class="LenderAccordion-header">
				<div style="display: flex; justify-content: space-between; align-items: flex-start; margin: 10px; flex-direction: column">
					<span style="font-size: 1rem; font-weight: 600">{{ lenderName?.toUpperCase() }}</span>

					<span
						style="font-size: 0.75rem"
						v-if="$global.isAdminView"
						>{{ props.applications.length }} application(s)</span
					>
				</div>

				<div class="lender-container-buttons">
					<button
						v-if="lenderCode == 'ALLY' && props.applications.some(x => x.isSoftPull && x.status == CreditApplicationDecision.Denied)"
						class="hovered-desc ignore-all-locks"
						style="display: flex; justify-content: center; align-items: center; width: 122px; justify-self: flex-end"
						data-title="Passthrough"
						:disabled="loadingDynamicFieldsLocally || props.loadingCustomFields || props.fimenu.isDealLocked()"
						@click="e => handleOpenSubmitModal(e, true, true)"
					>
						<span v-if="!loadingPassthrough">Pass-through</span>
						<i
							class="fas fa-spinner fa-spin"
							v-else
						></i>
					</button>

					<button
						v-if="props.lendersThatAllowAutoSubmit.some(x => currentLenderAlternativeCodes.some(a => a.code == x))"
						class="hovered-desc ignore-all-locks"
						style="display: flex; justify-content: center; align-items: center; width: 25px"
						data-title="Re-submit"
						:disabled="loadingDynamicFieldsLocally || props.loadingCustomFields || props.fimenu.isDealLocked()"
						@click="e => handleOpenSubmitModal(e, false, false)"
					>
						<i
							class="fas fa-redo"
							v-if="!loadingResubmit"
						></i>
						<i
							class="fas fa-spinner fa-spin"
							v-else
						></i>
					</button>

					<i
						v-if="anyApplicationHasMessages"
						class="notification-icon fas fa-circle"
						style="color: white; font-size: 10px"
					/>
				</div>
			</div>
		</template>

		<template #accordionBody>
			<div
				v-for="(application, index) in applications"
				:key="index"
				class="ApplicationsBox no-margin-grid"
			>
				<PCALenderApplications
					:application="application"
					:lendersList="lendersList"
					:send="send"
					:reserveProfitCalcs="reserveProfitCalcs"
					:fimenu="fimenu"
					:dealHasNoLender="dealHasNoLender"
					:showBalloonIcon="showBalloonIcon"
					:handleSelectLender="handleSelectLender"
					:balloonLenders="balloonLenders"
					:lenderCode="lenderCode"
					:anyApplicationIsSelected="anyApplicationIsSelected"
					@ApplicationSelected="handleApplicationSelected"
					:saturnMessages="getApplicationMessages(application)"
				/>
			</div>

			<div
				v-if="fimenu.preSelectedLenders.some(preSelectedLenderCode => preSelectedLenderCode == lenderCode)"
				class="ApplicationsBox no-margin-grid"
			>
				<PCALenderApplications
					:application="new FIMenuCreditApplication()"
					:lendersList="lendersList"
					:send="send"
					:reserveProfitCalcs="reserveProfitCalcs"
					:fimenu="fimenu"
					:dealHasNoLender="dealHasNoLender"
					:showBalloonIcon="showBalloonIcon"
					:handleSelectLender="handleSelectLender"
					:balloonLenders="balloonLenders"
					:lenderCode="lenderCode"
					:isLenderEntry="true"
					:anyApplicationIsSelected="anyApplicationIsSelected"
					@ApplicationSelected="handleApplicationSelected"
					:saturnMessages="getApplicationMessages()"
				/>
			</div>
		</template>
	</AccordionSection>
</template>

<script lang="ts">
	export interface IPCALenderContainerProps {
		lenderName: string;
		applications: FIMenuCreditApplication[];
		onlyShowBalloonLenders: boolean;
		lendersList: VersionsLender[];
		send: (lender: VersionsLender, submissionInformation: FIDealJacketAdditionalInformation) => void;
		reserveProfitCalcs: any;
		fimenu: FIMenu;
		dealHasNoLender: boolean;
		showBalloonIcon: (lenderCode: string) => boolean;
		handleSelectLender: (lender: VersionsLender, isApplication?: boolean, selectedProvider?: FinanceChannelsProvider) => void;
		balloonLenders: string[];
		openSendModal: (lender: VersionsLender | null, forcePassthrough: boolean) => void;
		fetchDynamicFields: (lenderCode: string) => Promise<boolean>;
		lenderCode: string;
		anyApplicationIsSelected: boolean;
		lendersThatAllowAutoSubmit: string[];
		loadingCustomFields: boolean;
	}
</script>

<script setup lang="ts">
	// #region Imports
	import { computed, inject, ref } from 'vue';
	import { CreditApplicationDecision } from '@core/classes/CreditApplication';
	import FIDealJacketAdditionalInformation from '@core/classes/FIDealJacketAdditionalInformation';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuCreditApplication from '@core/classes/FIMenuCreditApplication';
	import { DocAssociation, FinanceChannelsProvider } from '@core/classes/SharedEnums';
	import VersionsLender from '@core/classes/LenderVersionClass';

	import AccordionSection from '@core/components/AccordionSection.vue';
	import IconWithCount from '@/components/IconWithCount.vue';
	import PCALenderApplications from '@/components/PCALenderApplications.vue';
	import util from '@core/services/util';
	import LenderStatusIcons from '@/components/LenderStatusIcons.vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import { useMessagesStore } from '@core/stores/MessagesStore';

	// #endregion
	const emit = defineEmits(['ApplicationSelected']);
	const messagesStore = useMessagesStore();

	const $global = inject<SaturnGlobalPlugin>('$global');

	// #region States
	const props = withDefaults(defineProps<IPCALenderContainerProps>(), {
		lendersThatAllowAutoSubmit: () => [],
	});
	// #endregion

	const getApplicationMessages = (application?: FIMenuCreditApplication) => {
		if (application?.id == null) return [];
		return messagesStore.getDealMessages().filter(m => m.relationships.some(r => r.type == DocAssociation.CreditApplication && r.id == application.id));
	};

	const anyApplicationHasMessages = computed(() => {
		return props.applications.some(x => getApplicationMessages(x).length > 0);
	});
	// #region Computed
	const approvedApplications = computed(() => {
		return props.applications.filter(a => a.status === CreditApplicationDecision.Approved).length;
	});

	const conditionalApplications = computed(() => {
		return props.applications.filter(a => a.status === CreditApplicationDecision.Condition).length;
	});

	const deniedApplications = computed(() => {
		return props.applications.filter(a => a.status === CreditApplicationDecision.Denied).length;
	});

	const appOrLenderSelected = computed(() => {
		return props.applications.some(a => a.selected == true) || (props.lenderCode == props.fimenu.lender.lenderCode && props.fimenu.lender.isActiveOnDeal);
	});
	/**
	 * Resubmits a credit application to the lender
	 * @param application The credit application to resubmit
	 */
	//  const reSubmitApplication = () => {
	//     util.modalConfirmation('Are you sure you want to resubmit this credit application?', 'Yes', () => {
	//         //Get the lender from the application.
	//         const lender = props.lendersList.find(x => x.lenderCode == application.lender.lenderCode);
	//         if (!lender) return util.toastr('error', 'Error', 'Lender not found'); // If the lender is not found, return an error because we cannot submit.

	//         props.send(lender, application.submissionInformation);
	//     });
	// };

	const handleApplicationSelected = (application: FIMenuCreditApplication) => {
		emit('ApplicationSelected', application);
	};

	const loadingDynamicFieldsLocally = ref(false);

	const loadingPassthrough = ref(false);
	const loadingResubmit = ref(false);
	const handleOpenSubmitModal = async (event: Event, forcePassthrough: boolean = false, isPassthrough: boolean = false) => {
		// Disable opening the accordion.
		event.stopPropagation();

		if (props.fimenu.isDealLocked()) return util.toastr('error', 'Error', 'Cannot send credit applications. Deal is locked.');

		if (isPassthrough) {
			loadingPassthrough.value = true;
		} else {
			loadingResubmit.value = true;
		}

		loadingDynamicFieldsLocally.value = true;
		const success = await props.fetchDynamicFields(props.lenderCode);
		// If we get the dynamic fields successfully, we will open the send modal.
		// Else, display an error message.
		if (success) {
			props.openSendModal(
				props.lendersList.find(x => x.lenderCode == props.lenderCode),
				forcePassthrough,
			);
		} else {
			util.toastr('error', 'Error', 'Error fetching lender program codes. Please refresh the page and try again.');
		}

		loadingDynamicFieldsLocally.value = false;
		loadingPassthrough.value = false;
		loadingResubmit.value = false;
	};

	const lender = computed(() => {
		return props.lendersList.find(x => x.lenderCode == props.lenderCode);
	});

	const currentLenderAlternativeCodes = computed(() => {
		return lender.value?.alternativeLenderCodes ?? [];
	});
	// #endregion
</script>

<style>
	.LenderAccordion-header {
		width: 90%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		box-sizing: border-box;
	}
	.LenderAccordion .accordion-section-header {
		height: 75px !important;
	}

	.LenderAccordion .row-container {
		display: flex;
		font-size: 1em;
		align-items: center;
		width: 100%;
		border: 1px solid var(--border-color);
		padding: 5px;
		border-radius: 5px;
	}
	.LenderAccordion .row-container span {
		font-size: 0.75em;
		color: var(--main-color);
		margin: auto;
		text-align: center;
	}
	.LenderAccordion .IconWithCount-container .stack-body {
		font-size: 1em !important;
	}
	.LenderAccordion .row-container span.bolded {
		font-weight: 600;
	}

	.LenderAccordion .row-container span.number-count {
		min-width: 19px;
		padding: 0 5px 0 0px;
	}

	.LenderAccordion-header .lender-container-buttons {
		display: flex;
		flex-direction: row;
		gap: 10px;
	}
</style>
